import { Head, Link } from '@inertiajs/react';
import { Adventure, formatDuration } from '@/types/adventures';

/**
 * Props for the Adventures Index page.
 */
interface Props {
    adventures: Adventure[];
    inProgressCount: number;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}

/**
 * User Adventures Index page - displays available adventures to attempt.
 */
export default function AdventuresIndex({ adventures, inProgressCount, statLabels, skillLabels }: Props) {
    return (
        <>
            <Head title="Adventures" />

            <div className="min-h-screen bg-slate-900 py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-white">Adventures</h1>
                            <p className="text-gray-400 text-sm mt-1">
                                Send your characters on quests to earn rewards
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            {inProgressCount > 0 && (
                                <Link
                                    href="/adventures/attempts"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                >
                                    🎲 My Adventures ({inProgressCount})
                                </Link>
                            )}
                            <Link
                                href="/map"
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                ← Back to Map
                            </Link>
                        </div>
                    </div>

                    {/* Adventures Grid */}
                    {adventures.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <p className="text-lg">No adventures available at this time.</p>
                            <p className="text-sm mt-2">Check back later for new quests!</p>
                        </div>
                    ) : (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {adventures.map((adventure) => (
                                <AdventureCard
                                    key={adventure.id}
                                    adventure={adventure}
                                    statLabels={statLabels}
                                    skillLabels={skillLabels}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

/**
 * AdventureCard - Displays a single adventure with key info.
 */
function AdventureCard({
    adventure,
    statLabels,
    skillLabels,
}: {
    adventure: Adventure;
    statLabels: Record<string, string>;
    skillLabels: Record<string, string>;
}) {
    // Get challenge labels
    const getChallengeLabel = (statType: string): string => {
        return statLabels[statType] || skillLabels[statType] || statType;
    };

    // Get difficulty color
    const getDifficultyColor = (difficulty: number): string => {
        if (difficulty >= 50) return 'text-red-400';
        if (difficulty >= 30) return 'text-orange-400';
        if (difficulty >= 15) return 'text-yellow-400';
        return 'text-green-400';
    };

    // Count reward types (using the value property from the reward_type object)
    const rewardSummary = adventure.rewards.reduce((acc, reward) => {
        const typeValue = reward.reward_type.value;
        acc[typeValue] = (acc[typeValue] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    return (
        <Link
            href={`/adventures/${adventure.id}`}
            className="block bg-slate-800 border border-slate-700 rounded-lg p-5 hover:border-purple-500 transition-colors group"
        >
            {/* Name and Description */}
            <h3 className="text-lg font-semibold text-white group-hover:text-purple-400 transition-colors">
                {adventure.name}
            </h3>
            {adventure.description && (
                <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                    {adventure.description}
                </p>
            )}

            {/* Stats Row */}
            <div className="flex items-center gap-4 mt-4 text-sm">
                {/* Difficulty */}
                <div className="flex items-center gap-1">
                    <span className="text-gray-500">⚔️</span>
                    <span className={getDifficultyColor(adventure.total_difficulty)}>
                        {adventure.total_difficulty}
                    </span>
                </div>

                {/* Time */}
                <div className="flex items-center gap-1">
                    <span className="text-gray-500">⏱️</span>
                    <span className="text-gray-300">
                        {formatDuration(adventure.estimated_minutes || 0)}
                    </span>
                </div>

                {/* Challenges Count */}
                <div className="flex items-center gap-1">
                    <span className="text-gray-500">🎯</span>
                    <span className="text-gray-300">
                        {adventure.challenges.length} {adventure.challenges.length === 1 ? 'challenge' : 'challenges'}
                    </span>
                </div>
            </div>

            {/* Challenges Preview */}
            {adventure.challenges.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                    {adventure.challenges.slice(0, 3).map((challenge) => (
                        <span
                            key={challenge.id}
                            className="px-2 py-0.5 text-xs bg-slate-700 text-gray-300 rounded"
                            title={`${getChallengeLabel(challenge.stat_type)}: ${challenge.difficulty}`}
                        >
                            {getChallengeLabel(challenge.stat_type).split(' ')[0]}
                        </span>
                    ))}
                    {adventure.challenges.length > 3 && (
                        <span className="px-2 py-0.5 text-xs bg-slate-700 text-gray-400 rounded">
                            +{adventure.challenges.length - 3} more
                        </span>
                    )}
                </div>
            )}

            {/* Rewards Preview */}
            {adventure.rewards.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>Rewards:</span>
                        {rewardSummary.item && (
                            <span className="px-2 py-0.5 bg-purple-900/50 text-purple-300 rounded">
                                🎁 {rewardSummary.item} item{rewardSummary.item > 1 ? 's' : ''}
                            </span>
                        )}
                        {rewardSummary.material && (
                            <span className="px-2 py-0.5 bg-amber-900/50 text-amber-300 rounded">
                                📦 {rewardSummary.material} material{rewardSummary.material > 1 ? 's' : ''}
                            </span>
                        )}
                        {rewardSummary.xp && (
                            <span className="px-2 py-0.5 bg-green-900/50 text-green-300 rounded">
                                ⭐ XP
                            </span>
                        )}
                    </div>
                </div>
            )}
        </Link>
    );
}

